import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Fuel } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useReadingTypoGuard } from './cost/ReadingTypoGuard';

const COST_TYPES = [
  { value: 'fuel', label: 'Fuel' },
  { value: 'tyres', label: 'Tyres' },
  { value: 'service', label: 'Service' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'labour', label: 'Labour' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'license', label: 'License' },
  { value: 'other', label: 'Other' },
];

const EditCostDialog = ({ open, onOpenChange, cost, onSuccess }) => {
  const [formData, setFormData] = useState({
    vehicle_id: '',
    cost_type: '',
    amount: '',
    description: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    odometer: '',
    hours: '',
    liters: '',
    price_per_liter: '',
    supplier_id: '',
    vendor: '',
    invoice_number: '',
    order_number: '',
    discount: '',
    notes: '',
    service_interval_km: '',
    next_service_reading: '',
    next_service_date: '',
  });
  const [saving, setSaving] = useState(false);
  const [priceDiscrepancy, setPriceDiscrepancy] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vehicle, setVehicle] = useState(null);

  useEffect(() => {
    if (open) {
      api.getSuppliers().then(setSuppliers).catch(() => setSuppliers([]));
      api.getVehicles().then(setVehicles).catch(() => setVehicles([]));
    }
  }, [open]);

  useEffect(() => {
    if (cost && open) {
      setUserTouchedNextService(false);
      setUserTouchedNextDate(false);
      setFormData({
        vehicle_id: cost.vehicle_id || '',
        cost_type: cost.cost_type,
        amount: cost.amount.toString(),
        description: cost.description || '',
        date: cost.date,
        odometer: cost.odometer?.toString() || '',
        hours: cost.hours?.toString() || '',
        liters: cost.liters?.toString() || '',
        price_per_liter: cost.price_per_liter?.toString() || '',
        supplier_id: cost.supplier_id || '',
        vendor: cost.vendor || cost.supplier_name || '',
        invoice_number: cost.invoice_number || '',
        order_number: cost.order_number || '',
        discount: cost.discount?.toString() || '',
        notes: cost.notes || '',
        service_interval_km: cost.service_interval_km?.toString() || cost.service_interval_hours?.toString() || '',
        next_service_reading: '',
        next_service_date: cost.next_service_expected_date || '',
      });
    }
  }, [cost, open]);

  // Keep the vehicle state in sync with the current selection so isHoursBased reflects the picker.
  useEffect(() => {
    if (!formData.vehicle_id) { setVehicle(null); return; }
    const v = vehicles.find(x => x.id === formData.vehicle_id);
    if (v) { setVehicle(v); return; }
    api.getVehicle(formData.vehicle_id).then(setVehicle).catch(() => setVehicle(null));
  }, [formData.vehicle_id, vehicles]);

  const isHoursBased = vehicle?.usage_unit === 'hours' || cost?.usage_unit === 'hours';
  // Vehicles flagged as excluded from the fleet average (private/yard vehicles) do
  // not track km/L, so the meter reading is optional on edit.
  const isMeterExempt = vehicle?.include_in_fleet_avg === false;

  // When editing a Service entry, pre-fill the interval + next-service
  // reading ONCE based on the vehicle's history. Do NOT overwrite the
  // user's manual edits — a `userTouchedNextService` flag freezes the
  // auto-compute after they type.
  const [userTouchedNextService, setUserTouchedNextService] = useState(false);
  const [userTouchedNextDate, setUserTouchedNextDate] = useState(false);
  const { guard: guardReading, dialog: readingTypoDialog } = useReadingTypoGuard();

  useEffect(() => {
    if (formData.cost_type !== 'service') return;
    const vehicleInterval = isHoursBased
      ? (vehicle?.service_interval_hours || cost?.service_interval_hours || 500)
      : (vehicle?.service_interval_km || cost?.service_interval_km || 10000);
    const explicitNext = isHoursBased
      ? (cost?.next_service_hours ?? vehicle?.next_service_hours)
      : (cost?.next_service_km ?? vehicle?.next_service_km);
    const current = isHoursBased ? formData.hours : formData.odometer;
    setFormData(prev => {
      const interval = prev.service_interval_km || String(vehicleInterval);
      // If the user has typed something, keep it. Otherwise prefer the
      // vehicle's explicit next-service target, then auto-compute.
      let expected = prev.next_service_reading;
      if (!userTouchedNextService) {
        if (explicitNext != null) expected = String(Math.round(explicitNext));
        else if (current && interval) expected = String(Math.round(parseFloat(current) + parseFloat(interval)));
      }
      if (prev.service_interval_km === interval && prev.next_service_reading === expected) return prev;
      return { ...prev, service_interval_km: interval, next_service_reading: expected };
    });
  }, [formData.cost_type, formData.odometer, formData.hours, isHoursBased, vehicle, cost, userTouchedNextService]);

  // Auto-derive price_per_liter from liters + amount for fuel entries.
  useEffect(() => {
    if (formData.cost_type === 'fuel' && formData.liters && formData.amount) {
      const liters = parseFloat(formData.liters);
      const totalAmount = parseFloat(formData.amount);
      if (liters > 0 && totalAmount > 0) {
        const derived = (totalAmount / liters).toFixed(3);
        setFormData(prev => (prev.price_per_liter === derived ? prev : { ...prev, price_per_liter: derived }));
      }
    }
    setPriceDiscrepancy(null);
  }, [formData.liters, formData.amount, formData.cost_type]);

  // Optimized field update handler
  const updateField = (field, value) => {
    setFormData(prev => {
      if (prev[field] === value) return prev;
      return { ...prev, [field]: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vehicle_id) {
      toast.error('Please select a vehicle');
      return;
    }
    if (!formData.cost_type || !formData.amount) {
      toast.error('Please fill in required fields');
      return;
    }

    // Odometer OR hours (based on vehicle) is REQUIRED unless the vehicle is
    // excluded from the fleet average (private/yard use).
    const usageReading = isHoursBased ? formData.hours : formData.odometer;
    if (!usageReading && !isMeterExempt) {
      toast.error(`${isHoursBased ? 'Hours' : 'Odometer'} reading is required for all cost entries`);
      return;
    }

    // Additional validation for fuel entries
    if (formData.cost_type === 'fuel') {
      if (!formData.liters) {
        toast.error('Liters is required for fuel entries');
        return;
      }
      if (!formData.vendor) {
        toast.error('Supplier is required for fuel entries');
        return;
      }
    }

    await guardReading(
      { vehicleId: formData.vehicle_id, date: formData.date, reading: usageReading, excludeId: cost.id },
      saveEdit,
    );
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.updateCost(cost.id, {
        vehicle_id: formData.vehicle_id || null,
        cost_type: formData.cost_type,
        amount: parseFloat(formData.amount),
        description: formData.description || null,
        date: formData.date,
        odometer: formData.odometer ? parseFloat(formData.odometer) : null,
        hours: formData.hours ? parseFloat(formData.hours) : null,
        liters: formData.liters ? parseFloat(formData.liters) : null,
        price_per_liter: formData.price_per_liter ? parseFloat(formData.price_per_liter) : null,
        supplier_id: formData.supplier_id || null,
        vendor: formData.vendor || null,
        invoice_number: formData.invoice_number || null,
        order_number: formData.order_number || null,
        discount: formData.discount ? parseFloat(formData.discount) : null,
        notes: formData.notes || null,
        // Service-only fields — rolled into the vehicle on the backend so
        // the Cost Budget forecast recomputes next-service date & cost.
        service_interval_km: (formData.cost_type === 'service' && !isHoursBased && formData.service_interval_km)
          ? parseInt(formData.service_interval_km, 10) : null,
        service_interval_hours: (formData.cost_type === 'service' && isHoursBased && formData.service_interval_km)
          ? parseInt(formData.service_interval_km, 10) : null,
        ...(userTouchedNextDate && formData.cost_type === 'service'
          ? { next_service_expected_date: formData.next_service_date || '' } : {}),
        // Explicit next-service target the operator typed. The Cost Budget
        // forecast prefers these over the computed `last_service + interval`.
        next_service_km: (userTouchedNextService && formData.cost_type === 'service' && !isHoursBased && formData.next_service_reading)
          ? parseFloat(formData.next_service_reading) : null,
        next_service_hours: (userTouchedNextService && formData.cost_type === 'service' && isHoursBased && formData.next_service_reading)
          ? parseFloat(formData.next_service_reading) : null,
      });
      toast.success('Cost entry updated');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update cost');
    } finally {
      setSaving(false);
    }
  };

  const isFuelEntry = formData.cost_type === 'fuel';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            {isFuelEntry && <Fuel className="w-5 h-5 text-orange-500" />}
            Edit Cost Entry
          </DialogTitle>
          <DialogDescription>
            Update the expense details
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Vehicle picker — required. Lets you fix legacy 'Unknown' entries by re-assigning. */}
          <div className="space-y-2">
            <Label>Vehicle *</Label>
            <Select
              value={formData.vehicle_id}
              onValueChange={(value) => setFormData(prev => ({ ...prev, vehicle_id: value }))}
            >
              <SelectTrigger data-testid="edit-cost-vehicle-select">
                <SelectValue placeholder="Select vehicle" />
              </SelectTrigger>
              <SelectContent>
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name}{v.registration ? ` — ${v.registration}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formData.vehicle_id && !vehicles.some(v => v.id === formData.vehicle_id) && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                The originally linked vehicle no longer exists — pick the correct one to fix this row.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cost Type *</Label>
              <Select 
                value={formData.cost_type} 
                onValueChange={(value) => setFormData({ ...formData, cost_type: value })}
              >
                <SelectTrigger data-testid="edit-cost-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {COST_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Amount (R) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => updateField('amount', e.target.value)}
                placeholder="0.00"
                className="font-data"
                data-testid="edit-cost-amount-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Date *</Label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => updateField('date', e.target.value)}
              className="font-data"
              data-testid="edit-cost-date-input"
            />
          </div>

          {/* Fuel-specific fields */}
          {isFuelEntry && (
            <div className="border-t border-b py-4 my-4 bg-orange-50/50 -mx-6 px-6">
              <h4 className="font-medium text-sm text-orange-800 mb-3 flex items-center gap-2">
                <Fuel className="w-4 h-4" />
                Fuel Entry Details
              </h4>
              
              {/* Odometer - REQUIRED for fuel unless vehicle is fleet-average exempt */}
              <div className="space-y-2 mb-4">
                <Label className="text-orange-900">
                  {isMeterExempt ? 'Odometer Reading (km) — optional' : 'Odometer Reading (km) *'}
                </Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={formData.odometer}
                  onChange={(e) => updateField('odometer', e.target.value)}
                  placeholder="Current odometer reading"
                  className="font-data border-orange-200 focus:border-orange-400"
                  data-testid="edit-cost-odometer-input"
                  required={!isMeterExempt}
                />
                {isMeterExempt && (
                  <p className="text-xs text-slate-600 italic" data-testid="edit-meter-exempt-hint-fuel">
                    This vehicle is excluded from the fleet average — no meter reading needed.
                  </p>
                )}
              </div>

              <div className="space-y-2 mb-4">
                <Label className="text-orange-900">Liters *</Label>
                <Input
                  type="number"
                  step="any"
                  min="0"
                  value={formData.liters}
                  onChange={(e) => updateField('liters', e.target.value)}
                  placeholder="e.g. 161.539"
                  className="font-data border-orange-200"
                  data-testid="edit-cost-liters-input"
                />
                <p className="text-xs text-orange-600">Enter liters with decimals (e.g., 161.539). Price/litre is auto-calculated.</p>
              </div>

              {formData.liters && formData.amount && parseFloat(formData.liters) > 0 && (
                <p className="text-xs text-blue-700 mb-4 font-data" data-testid="edit-fuel-derived-price">
                  Auto-calculated price/litre: R {(parseFloat(formData.amount) / parseFloat(formData.liters)).toFixed(3)}
                </p>
              )}

              {/* Supplier and Invoice */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-orange-900">Supplier *</Label>
                  {suppliers.length > 0 ? (
                    <Select
                      value={formData.supplier_id}
                      onValueChange={(v) => {
                        const s = suppliers.find(x => x.id === v);
                        setFormData(prev => ({ ...prev, supplier_id: v, vendor: s?.name || prev.vendor }));
                      }}
                    >
                      <SelectTrigger data-testid="edit-cost-supplier-select">
                        <SelectValue placeholder="Select supplier" />
                      </SelectTrigger>
                      <SelectContent>
                        {suppliers.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={formData.vendor}
                      onChange={(e) => updateField('vendor', e.target.value)}
                      placeholder="e.g., Shell, BP, Engen"
                      data-testid="edit-cost-vendor-input"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-orange-900">Invoice Number</Label>
                  <Input
                    value={formData.invoice_number}
                    onChange={(e) => updateField('invoice_number', e.target.value)}
                    placeholder="INV-00001"
                    className="font-data"
                    data-testid="edit-cost-invoice-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-orange-900">Order Nr</Label>
                  <Input
                    value={formData.order_number || ''}
                    onChange={(e) => updateField('order_number', e.target.value)}
                    placeholder="PO-00001"
                    className="font-data"
                    data-testid="edit-cost-order-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Non-fuel: Odometer/Hours REQUIRED, vendor and invoice optional */}
          {!isFuelEntry && formData.cost_type && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>
                  {isHoursBased
                    ? (isMeterExempt ? 'Hours Reading (Hrs) — optional' : 'Hours Reading (Hrs) *')
                    : (isMeterExempt ? 'Odometer Reading (km) — optional' : 'Odometer Reading (km) *')}
                </Label>
                <Input
                  type="number"
                  step={isHoursBased ? '0.1' : '1'}
                  min="0"
                  value={isHoursBased ? formData.hours : formData.odometer}
                  onChange={(e) => updateField(isHoursBased ? 'hours' : 'odometer', e.target.value)}
                  placeholder={isHoursBased ? 'Current hours reading' : 'Current odometer reading'}
                  className="font-data"
                  data-testid="edit-cost-odometer-input"
                  required={!isMeterExempt}
                />
                <p className="text-xs text-slate-500">
                  {isMeterExempt
                    ? 'This vehicle is excluded from the fleet average — no meter reading needed.'
                    : `${isHoursBased ? 'Hours' : 'Odometer'} is required for all cost entries`}
                </p>
              </div>

              {/* Blue Service Details box — only shown when cost_type is 'service'.
                  Captures/edits the interval, expected next-service reading and
                  projected next-service date so the Cost Budget stays accurate. */}
              {formData.cost_type === 'service' && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-sm p-4 space-y-3 mb-4" data-testid="edit-service-details-box">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/></svg>
                    <h5 className="font-display font-black text-blue-950 text-base uppercase tracking-tight">Service Details</h5>
                  </div>
                  <p className="text-xs text-blue-800">
                    Saving these rolls the vehicle's service history forward so the Cost Budget forecast recomputes next-service date &amp; cost.
                  </p>

                  {/* Last service summary pulled from vehicle doc */}
                  {(vehicle?.last_service_date || vehicle?.last_service_odometer || vehicle?.last_service_hours) && (
                    <div className="bg-white border border-blue-200 rounded-sm p-2 text-xs text-blue-900 font-mono space-y-0.5" data-testid="edit-last-service-summary">
                      <div>Last service: <span className="font-bold">{vehicle?.last_service_date || '—'}</span></div>
                      <div>
                        At {isHoursBased
                          ? `${(vehicle?.last_service_hours ?? 0).toLocaleString()} hrs`
                          : `${(vehicle?.last_service_odometer ?? 0).toLocaleString()} km`}
                        {formData.odometer && !isHoursBased && vehicle?.last_service_odometer && parseFloat(formData.odometer) >= vehicle.last_service_odometer
                          ? ` → ${(parseFloat(formData.odometer) - vehicle.last_service_odometer).toLocaleString()} km since`
                          : ''}
                        {formData.hours && isHoursBased && vehicle?.last_service_hours && parseFloat(formData.hours) >= vehicle.last_service_hours
                          ? ` → ${(parseFloat(formData.hours) - vehicle.last_service_hours).toLocaleString()} hrs since`
                          : ''}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-blue-900 text-xs">{isHoursBased ? 'Interval (hours)' : 'Interval (km)'}</Label>
                      <Input
                        type="number" step="1" min="1"
                        value={formData.service_interval_km}
                        onChange={(e) => {
                          const next = e.target.value;
                          const current = parseFloat(isHoursBased ? formData.hours : formData.odometer);
                          const nextRead = current && next ? String(Math.round(current + parseFloat(next))) : formData.next_service_reading;
                          setFormData(prev => ({ ...prev, service_interval_km: next, next_service_reading: nextRead }));
                        }}
                        placeholder={isHoursBased ? '500' : '10000'}
                        className="font-data border-blue-300 bg-white"
                        data-testid="edit-service-interval-input"
                      />
                      {isHoursBased && (
                        <div className="flex flex-wrap gap-1 pt-1">
                          {[150, 200, 250, 300, 350, 400, 450, 500].map((h) => (
                            <button
                              key={h}
                              type="button"
                              onClick={() => {
                                const current = parseFloat(formData.hours);
                                setFormData(prev => ({ ...prev, service_interval_km: String(h), next_service_reading: current ? String(Math.round(current + h)) : prev.next_service_reading }));
                              }}
                              className={`px-2 py-0.5 rounded-full text-[11px] font-data border transition-colors ${String(formData.service_interval_km) === String(h) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-800 border-blue-300 hover:bg-blue-100'}`}
                              data-testid={`edit-service-interval-chip-${h}`}
                            >
                              {h}h
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-blue-900 text-xs">
                        Next Service {isHoursBased ? 'Hours' : 'Odometer'}
                      </Label>
                      <Input
                        type="number"
                        step={isHoursBased ? '1' : '100'}
                        min="0"
                        value={formData.next_service_reading}
                        onChange={(e) => { setUserTouchedNextService(true); updateField('next_service_reading', e.target.value); }}
                        placeholder={isHoursBased ? 'e.g. 9500' : 'e.g. 72000'}
                        className="font-data border-blue-300 bg-white"
                        data-testid="edit-next-service-reading-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-blue-900 text-xs">Expected Next Service Date</Label>
                    <Input
                      type="date"
                      value={formData.next_service_date}
                      onChange={(e) => { setUserTouchedNextDate(true); updateField('next_service_date', e.target.value); }}
                      className="font-data border-blue-300 bg-white"
                      data-testid="edit-next-service-date-input"
                    />
                    <p className="text-xs text-blue-700">
                      Auto-recalculated from the interval and your recent average usage whenever the reading or interval changes. Clear it to recalculate now; only a date you type yourself is kept.
                    </p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Supplier</Label>
                  {suppliers.length > 0 ? (
                    <Select
                      value={formData.supplier_id}
                      onValueChange={(v) => {
                        const s = suppliers.find(x => x.id === v);
                        setFormData(prev => ({ ...prev, supplier_id: v, vendor: s?.name || prev.vendor }));
                      }}
                    >
                      <SelectTrigger data-testid="edit-cost-supplier-select-nonfuel">
                        <SelectValue placeholder="Select supplier" />
                      </SelectTrigger>
                      <SelectContent>
                        {suppliers.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={formData.vendor}
                      onChange={(e) => updateField('vendor', e.target.value)}
                      placeholder="Service provider"
                      data-testid="edit-cost-vendor-input"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Invoice Number</Label>
                  <Input
                    value={formData.invoice_number}
                    onChange={(e) => updateField('invoice_number', e.target.value)}
                    placeholder="INV-00001"
                    className="font-data"
                    data-testid="edit-cost-invoice-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Order Nr</Label>
                  <Input
                    value={formData.order_number || ''}
                    onChange={(e) => updateField('order_number', e.target.value)}
                    placeholder="PO-00001"
                    className="font-data"
                    data-testid="edit-cost-order-input"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Description</Label>
            <Input
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder={isFuelEntry ? "e.g., Full tank fill-up" : "e.g., Oil change, brake pads"}
              data-testid="edit-cost-description-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              placeholder="Additional notes..."
              rows={2}
              data-testid="edit-cost-notes-input"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-gray-800 hover:bg-gray-700"
              disabled={saving}
              data-testid="update-cost-btn"
            >
              {saving ? 'Saving...' : 'Update'}
            </Button>
          </div>
        </form>
      </DialogContent>
      {readingTypoDialog}
    </Dialog>
  );
};

export default EditCostDialog;
